import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../component-styles/ReportsShared.css';
import '../../component-styles/ReportsFolder.css';
import API_BASE from '../../utils/api.js';
import { exportRowsToExcel } from '../../utils/excelExport.js';
import logoImg from '../../assets/default-if-logo.png';
import { getFreightDestinationOptions, getFreightDestinationLabel } from '../../utils/freightDestinations.js';
import { BUILT_IN_PO_STATUSES, normalizeCustomStatuses } from '../../utils/orderStatuses.js';
import {
  applyTrackerEdit,
  itemStatusChoices,
  previousTrackerValues,
  replacePurchaseOrderRows,
} from '../../utils/procurementTrackerEdits.js';

const ALL_COLUMNS = [
  { key: 'image',           label: 'Image' },
  { key: 'itemName',        label: 'Item Name' },
  { key: 'vendor',          label: 'Vendor' },
  { key: 'project',         label: 'Project' },
  { key: 'room',            label: 'Room' },
  { key: 'qty',             label: 'Qty' },
  { key: 'status',          label: 'Status' },
  { key: 'delivery',        label: 'Delivery' },
  { key: 'dateOrdered',     label: 'Date Ordered' },
  { key: 'estDelivery',     label: 'Est. Delivery' },
  { key: 'confirmedShip',   label: 'Confirmed Ship' },
  { key: 'actualDelivery',  label: 'Actual Delivery' },
  { key: 'poNumber',        label: 'PO #' },
  { key: 'poBalance',       label: 'PO Balance' },
  { key: 'tracking',        label: 'Tracking' },
  { key: 'shippingTo',      label: 'Shipping To' },
  { key: 'notes',           label: 'Notes' },
];

// Columns size to their content until the first resize, which freezes every
// visible column at its current width. A column shown later with no saved
// width gets the fallback.
const MIN_COL_WIDTH = 60;
const FALLBACK_COL_WIDTH = 150;

function loadColWidths(key) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '{}');
    const known = new Set(ALL_COLUMNS.map((c) => c.key));
    return Object.fromEntries(
      Object.entries(parsed || {}).filter(
        ([col, width]) => known.has(col) && Number.isFinite(width) && width >= MIN_COL_WIDTH
      )
    );
  } catch {
    return {};
  }
}

function saveColWidths(key, widths) {
  try {
    if (Object.keys(widths).length) localStorage.setItem(key, JSON.stringify(widths));
    else localStorage.removeItem(key);
  } catch {
    // non-fatal — widths just won't persist
  }
}

/* --------------------------------------------------------
   PDF helpers
-------------------------------------------------------- */
async function toBase64(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const timer = setTimeout(() => reject(new Error('timeout')), 6000);
    img.onload = () => {
      clearTimeout(timer);
      const MAX = 200;
      const ratio = Math.min(MAX / img.naturalWidth, MAX / img.naturalHeight, 1);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.naturalWidth * ratio));
      canvas.height = Math.max(1, Math.round(img.naturalHeight * ratio));
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      try {
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      } catch {
        reject(new Error('canvas tainted'));
      }
    };
    img.onerror = () => { clearTimeout(timer); reject(new Error('load error')); };
    img.src = url;
  });
}

async function loadLogoForPDF(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => reject(new Error('timeout')), 6000);
    img.onload = () => {
      clearTimeout(timer);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      try {
        resolve({ data: canvas.toDataURL('image/png'), aspect: img.naturalWidth / img.naturalHeight });
      } catch {
        reject(new Error('canvas tainted'));
      }
    };
    img.onerror = () => { clearTimeout(timer); reject(new Error('load error')); };
    img.src = url;
  });
}

/* --------------------------------------------------------
   Helpers
-------------------------------------------------------- */
function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '-';
  return Number(v).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

function fmtDate(v) {
  if (!v) return '-';
  const d = new Date(v);
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
}

function deliveryPillClass(shipToType) {
  if (shipToType === 'client_site') return 'client';
  if (!shipToType || shipToType === 'designer_warehouse') return 'warehouse';
  return 'custom';
}

function statusSlug(status) {
  return (status || 'none').toLowerCase().replace(/\s+/g, '-');
}

// '' groups items with no vendor assigned.
function vendorKey(row) {
  return (row.vendorName || '').trim().toLowerCase();
}

// Editable cells sit inside a row that opens its PO on click/Enter — keep
// interactions with the editor from also navigating away.
const stopRowNavigation = {
  onClick: (e) => e.stopPropagation(),
  onKeyDown: (e) => e.stopPropagation(),
};

// Commits on blur or Enter rather than on every keystroke, so typing a date or
// tracking number doesn't fire a save per character. Escape discards the draft.
function TrackerInlineInput({ type = 'text', value, onCommit, ariaLabel, placeholder }) {
  const normalized = type === 'date' ? (value ? String(value).slice(0, 10) : '') : value || '';
  const [draft, setDraft] = useState(normalized);

  useEffect(() => {
    setDraft(normalized);
  }, [normalized]);

  const commit = () => {
    const next = type === 'date' ? draft : draft.trim();
    if (next !== normalized) onCommit(next);
  };

  return (
    <input
      className="tracker-inline-input"
      type={type}
      value={draft}
      aria-label={ariaLabel}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          setDraft(normalized);
        }
      }}
    />
  );
}

/* ========================================================
   PROCUREMENT TRACKER PAGE
======================================================== */
export default function ProcurementTrackerPage() {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('all');
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterDelivery, setFilterDelivery] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [showColPanel, setShowColPanel] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [freightDestinations, setFreightDestinations] = useState([]);
  const [customOrderStatuses, setCustomOrderStatuses] = useState([]);
  const [saveError, setSaveError] = useState('');
  const [savingKeys, setSavingKeys] = useState(() => new Set());
  const colWidthsKey = `procurementTrackerColWidths_${userId || 'anon'}`;
  const [colWidths, setColWidths] = useState(() => loadColWidths(colWidthsKey));
  const colPanelRef = useRef(null);
  const tableRef = useRef(null);

  const deliveryOptions = useMemo(
    () => [{ value: 'all', label: 'All Destinations' }, ...getFreightDestinationOptions(freightDestinations)],
    [freightDestinations]
  );

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/settings/${userId}`);
        if (!res.ok) return;
        const settings = await res.json();
        setFreightDestinations(Array.isArray(settings?.freightDestinations) ? settings.freightDestinations : []);
        setCustomOrderStatuses(normalizeCustomStatuses(settings?.customOrderStatuses));
      } catch {
        // non-fatal — falls back to built-in destinations and statuses
      }
    })();
  }, [userId]);

  const show = (key) => !hiddenCols.has(key);

  function toggleCol(key) {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  const visibleColumns = ALL_COLUMNS.filter((c) => show(c.key));
  const columnsResized = Object.keys(colWidths).length > 0;
  const colWidth = (key) => colWidths[key] ?? FALLBACK_COL_WIDTH;

  // Drag a header's right edge to resize its column. The first drag measures
  // every visible column as rendered so the rest of the table holds still.
  function startColResize(e, key) {
    if (e.button !== 0) return;
    e.preventDefault();

    const measured = {};
    tableRef.current?.querySelectorAll('thead th[data-col]').forEach((th) => {
      measured[th.dataset.col] = Math.round(th.getBoundingClientRect().width);
    });
    const base = { ...measured, ...colWidths };
    const startX = e.clientX;
    const startWidth = base[key] ?? FALLBACK_COL_WIDTH;
    const handle = e.currentTarget;
    let latest = null;

    handle.setPointerCapture?.(e.pointerId);
    document.body.classList.add('col-resizing');

    const onMove = (ev) => {
      latest = { ...base, [key]: Math.max(MIN_COL_WIDTH, Math.round(startWidth + ev.clientX - startX)) };
      setColWidths(latest);
    };
    const onEnd = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onEnd);
      handle.removeEventListener('pointercancel', onEnd);
      document.body.classList.remove('col-resizing');
      if (latest) saveColWidths(colWidthsKey, latest);
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onEnd);
    handle.addEventListener('pointercancel', onEnd);
  }

  function resetColWidths() {
    setColWidths({});
    saveColWidths(colWidthsKey, {});
  }

  // Rows are per-item, but every item belongs to exactly one PO — clicking a
  // row opens that PO. Older rows may predate poId being returned, so guard.
  function openPurchaseOrder(row) {
    if (!row?.poId || !row?.projectId) return;
    navigate(`/projects/${row.projectId}/purchase-orders/${row.poId}`);
  }

  // Shows the edit immediately, then settles on what the server saved. On
  // failure only this edit is rolled back, so other rows edited meanwhile keep
  // their changes.
  async function saveTrackerEdit(row, changes) {
    const previous = previousTrackerValues(row, changes);
    const saveKeys = Object.keys(changes).map((field) => `${row.id}:${field}`);
    setSaveError('');
    setSavingKeys((prev) => new Set([...prev, ...saveKeys]));
    setRows((prev) => applyTrackerEdit(prev, row, changes));

    try {
      const res = await fetch(
        `${API_BASE}/api/purchase-orders/${encodeURIComponent(userId)}/by-id/${encodeURIComponent(row.poId)}/tracker`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('idToken')}`,
          },
          body: JSON.stringify({ itemId: row.itemId, changes }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save change.');
      setRows((prev) => replacePurchaseOrderRows(prev, row.poId, data.rows));
    } catch (err) {
      setRows((prev) => applyTrackerEdit(prev, row, previous));
      setSaveError(`${row.poNumber || 'Purchase order'}: ${err.message || 'Failed to save change.'}`);
    } finally {
      setSavingKeys((prev) => {
        const next = new Set(prev);
        saveKeys.forEach((key) => next.delete(key));
        return next;
      });
    }
  }

  const savingClass = (row, field) =>
    savingKeys.has(`${row.id}:${field}`) ? 'tracker-edit-cell is-saving' : 'tracker-edit-cell';

  useEffect(() => {
    if (!showColPanel) return;
    function handleClick(e) {
      if (colPanelRef.current && !colPanelRef.current.contains(e.target)) {
        setShowColPanel(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showColPanel]);

  /* --------------------------------------------------------
     Load procurement tracker data
  -------------------------------------------------------- */
  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API_BASE}/api/reports/procurement-tracker`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('idToken')}`,
          },
        });

        const data = await res.json();
        setRows(Array.isArray(data.rows) ? data.rows : []);
      } catch (err) {
        console.error('Failed to load procurement tracker', err);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [API_BASE]);

  /* --------------------------------------------------------
     Derived: unique projects for filter dropdown
  -------------------------------------------------------- */
  const projectOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => {
      if (r.projectId && !seen.has(r.projectId)) {
        seen.set(r.projectId, r.projectName || 'Untitled Project');
      }
    });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  // Rows only carry the vendor's name, so the filter keys on it — ignoring
  // case and stray spaces so hand-typed variants of one vendor group together.
  const vendorOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => {
      const key = vendorKey(r);
      if (key && !seen.has(key)) seen.set(key, r.vendorName.trim());
    });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const hasRowsWithoutVendor = useMemo(() => rows.some((r) => !vendorKey(r)), [rows]);

  // Every configured status is offered, plus anything the rows actually carry
  // that isn't configured (a custom status since removed, or legacy data), so
  // no row can end up unreachable by the filter.
  const statusOptions = useMemo(() => {
    const configured = [...BUILT_IN_PO_STATUSES, ...customOrderStatuses];
    const seen = new Set(configured);
    const extras = [];
    rows.forEach((r) => {
      if (r.orderStatus && !seen.has(r.orderStatus)) {
        seen.add(r.orderStatus);
        extras.push(r.orderStatus);
      }
    });
    return [...configured, ...extras.sort()];
  }, [rows, customOrderStatuses]);

  /* --------------------------------------------------------
     Filtered rows
  -------------------------------------------------------- */
  const tableRows = useMemo(() => {
    return rows.filter((r) => {
      if (filterProject !== 'all' && r.projectId !== filterProject) return false;
      if (filterVendor !== 'all' && vendorKey(r) !== filterVendor) return false;
      if (filterDelivery !== 'all' && (r.shipToType || 'designer_warehouse') !== filterDelivery) return false;
      if (filterStatus !== 'all' && (r.orderStatus || '') !== filterStatus) return false;
      const dateOrdered = r.dateOrdered ? String(r.dateOrdered).slice(0, 10) : '';
      if (startDate && dateOrdered && dateOrdered < startDate) return false;
      if (endDate && dateOrdered && dateOrdered > endDate) return false;
      return true;
    });
  }, [rows, filterProject, filterVendor, filterDelivery, filterStatus, startDate, endDate]);

  function exportExcel() {
    const headers = [
      'Item Name',
      'Vendor',
      'Project',
      'Room',
      'Qty',
      'Status',
      'Delivery Destination',
      'Date Ordered',
      'Est. Delivery',
      'Confirmed Ship',
      'Actual Delivery',
      'PO #',
      'PO Balance',
      'Tracking',
      'Shipping To',
      'Notes',
    ];

    const deliveryLabel = (type) => getFreightDestinationLabel(type, freightDestinations);

    const rowsForExport = tableRows.map((r) => [
      r.itemName || '',
      r.vendorName || '',
      r.projectName || '',
      r.room || '',
      Number(r.qty || 0),
      r.orderStatus || '',
      deliveryLabel(r.shipToType),
      r.dateOrdered ? String(r.dateOrdered).slice(0, 10) : '',
      r.estimatedDeliveryDate ? String(r.estimatedDeliveryDate).slice(0, 10) : '',
      r.confirmedShipDate ? String(r.confirmedShipDate).slice(0, 10) : '',
      r.actualDeliveryDate ? String(r.actualDeliveryDate).slice(0, 10) : '',
      r.poNumber || '',
      Number(r.poBalance || 0),
      r.trackingUrl || '',
      r.shippingTo || '',
      r.notes || '',
    ]);

    exportRowsToExcel({
      filename: 'purchase-order-status-report.xlsx',
      sheetName: 'PO Status',
      headers,
      rows: rowsForExport,
    });
  }

  async function exportPDF() {
    setPdfExporting(true);
    try {
      const { jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');

      // Load logo
      let logo = null;
      try { logo = await loadLogoForPDF(logoImg); } catch {}

      // Load item images (only if image column is visible)
      const hasImgCol = show('image');
      const imageMap = {};
      if (hasImgCol) {
        const urls = [...new Set(tableRows.map((r) => r.image).filter(Boolean))];
        await Promise.allSettled(
          urls.map(async (url) => {
            try { imageMap[url] = await toBase64(url); } catch {}
          })
        );
      }

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.width;   // 297
      const pageH = doc.internal.pageSize.height;  // 210
      const ML = 14;
      const MR = 14;
      const tableW = pageW - ML - MR;              // 269

      // ── Brand colours ──────────────────────────────────────────
      const C_CHARCOAL  = [17, 24, 39];    // #111827 — title text
      const C_HEADER_BG = [98, 121, 78];   // #62794e — table header bg
      const C_TEAL      = [165, 210, 125]; // #a5d27d — accent rule
      const C_WHITE     = [255, 255, 255];
      const C_OFF_WHITE = [247, 247, 245]; // #f7f7f5 — alt rows
      const C_BORDER    = [229, 231, 235]; // #e5e7eb
      const C_MUTED     = [107, 114, 128]; // #6b7280
      const C_TEXT      = [17, 24, 39];

      // ── Header ─────────────────────────────────────────────────
      const HEADER_BOT = 30;

      // Logo — top right
      if (logo) {
        const LOGO_H = 12;
        const LOGO_W = LOGO_H * logo.aspect;
        doc.addImage(logo.data, 'PNG', pageW - MR - LOGO_W, 7, LOGO_W, LOGO_H);
      }

      // Title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...C_CHARCOAL);
      doc.text('Procurement Tracker', ML, 15);

      // Subtitle row
      const subtitleParts = [];
      if (filterProject !== 'all') {
        const proj = projectOptions.find(([id]) => id === filterProject);
        if (proj) subtitleParts.push(`Project: ${proj[1]}`);
      }
      if (filterVendor !== 'all') {
        const vendor = vendorOptions.find(([key]) => key === filterVendor);
        subtitleParts.push(`Vendor: ${vendor ? vendor[1] : 'No vendor'}`);
      }
      if (filterDelivery !== 'all') {
        subtitleParts.push(deliveryOptions.find((o) => o.value === filterDelivery)?.label);
      }
      if (filterStatus !== 'all') {
        subtitleParts.push(`Status: ${filterStatus}`);
      }
      if (startDate || endDate) {
        subtitleParts.push(`Ordered ${startDate || '…'} - ${endDate || '…'}`);
      }
      subtitleParts.push(`${tableRows.length} item${tableRows.length !== 1 ? 's' : ''}`);

      doc.setFontSize(8.5);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...C_MUTED);
      doc.text(subtitleParts.filter(Boolean).join('  ·  '), ML, 23);
      doc.text(
        `Generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`,
        pageW - MR, 23, { align: 'right' }
      );

      // Teal accent rule
      doc.setDrawColor(...C_TEAL);
      doc.setLineWidth(0.6);
      doc.line(ML, HEADER_BOT, pageW - MR, HEADER_BOT);

      // ── Column definitions ─────────────────────────────────────
      // PDF uses a curated customer-facing subset — 4 internal-only columns
      // (Confirmed Ship, Actual Delivery, Tracking, Shipping To) are excluded
      // because they're not meaningful to customers and overflow the page.
      // The user's hiddenCols selection is still respected on top of this.
      const PDF_INCLUDE = new Set([
        'image', 'itemName', 'vendor', 'project', 'room', 'qty',
        'status', 'delivery', 'dateOrdered', 'estDelivery',
        'poNumber', 'poBalance', 'notes',
      ]);
      const colWeights = {
        image: 1.1, itemName: 2.4, vendor: 1.8, project: 1.5, room: 0.9,
        qty: 0.7, status: 1.2, delivery: 1.0, dateOrdered: 1.0, estDelivery: 1.0,
        poNumber: 1.0, poBalance: 1.1, notes: 2.8,
      };
      const PDF_COL_LABELS = {
        dateOrdered: 'Ordered',
        estDelivery: 'Est. Del.',
        poBalance: 'Balance',
      };
      const visibleCols = ALL_COLUMNS.filter(
        (c) => !hiddenCols.has(c.key) && PDF_INCLUDE.has(c.key)
      );
      const totalWeight = visibleCols.reduce((sum, c) => sum + (colWeights[c.key] || 1), 0);
      const perUnit = tableW / totalWeight;

      // Float widths — last column absorbs rounding remainder so total = tableW exactly
      const rawWidths = visibleCols.map((c) => (colWeights[c.key] || 1) * perUnit);
      const rawSum = rawWidths.reduce((a, b) => a + b, 0);
      rawWidths[rawWidths.length - 1] += tableW - rawSum;
      const columns = visibleCols.map((c, i) => ({
        header: PDF_COL_LABELS[c.key] || c.label,
        dataKey: c.key,
        cellWidth: rawWidths[i],
      }));

      // ── Row data — image cell MUST be '' so the URL isn't printed ──
      const deliveryLabel = (t) => getFreightDestinationLabel(t, freightDestinations);
      const bodyRows = tableRows.map((r) => ({
        image: '',
        itemName: r.itemName || '',
        vendor: r.vendorName || '',
        project: r.projectName || '',
        room: r.room || '-',
        qty: String(r.qty || 0),
        status: r.orderStatus || 'No status',
        delivery: deliveryLabel(r.shipToType),
        dateOrdered: fmtDate(r.dateOrdered),
        estDelivery: fmtDate(r.estimatedDeliveryDate),
        poNumber: r.poNumber || '',
        poBalance: formatCurrency(r.poBalance),
        notes: r.notes || '-',
      }));

      const ROW_H = hasImgCol ? 22 : 9;
      const PAD = 2.5;

      autoTable(doc, {
        startY: HEADER_BOT + 3,
        columns,
        body: bodyRows,
        rowPageBreak: 'avoid',
        margin: { top: 24, left: ML, right: MR },
        styles: {
          font: 'helvetica',
          fontSize: 8,
          cellPadding: { top: PAD, right: PAD, bottom: PAD, left: PAD },
          overflow: 'ellipsis',
          minCellHeight: ROW_H,
          valign: 'middle',
          textColor: C_TEXT,
          lineColor: C_BORDER,
          lineWidth: 0.15,
        },
        headStyles: {
          fillColor: C_HEADER_BG,
          textColor: C_WHITE,
          fontStyle: 'bold',
          fontSize: 7.5,
          minCellHeight: 9,
          cellPadding: { top: PAD, right: PAD, bottom: PAD, left: PAD },
        },
        bodyStyles: { fillColor: C_WHITE },
        alternateRowStyles: { fillColor: C_OFF_WHITE },
        didDrawCell(data) {
          if (hasImgCol && data.column.dataKey === 'image' && data.section === 'body') {
            const srcUrl = tableRows[data.row.index]?.image;
            const b64 = srcUrl ? imageMap[srcUrl] : null;
            if (b64) {
              const size = Math.min(ROW_H - PAD * 2, data.cell.width - PAD * 2);
              const x = data.cell.x + (data.cell.width - size) / 2;
              const y = data.cell.y + (data.cell.height - size) / 2;
              try { doc.addImage(b64, 'JPEG', x, y, size, size); } catch {}
            }
          }
        },
      });

      // ── Footer on every page ────────────────────────────────────
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);

        // Repeat header logo + rule on continuation pages
        if (i > 1) {
          if (logo) {
            const LOGO_H = 12;
            const LOGO_W = LOGO_H * logo.aspect;
            doc.addImage(logo.data, 'PNG', pageW - MR - LOGO_W, 5, LOGO_W, LOGO_H);
          }
          doc.setDrawColor(...C_TEAL);
          doc.setLineWidth(0.6);
          doc.line(ML, 19, pageW - MR, 19);
        }

        doc.setDrawColor(...C_BORDER);
        doc.setLineWidth(0.2);
        doc.line(ML, pageH - 12, pageW - MR, pageH - 12);
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...C_MUTED);
        doc.text('InteriorFolio — Confidential', ML, pageH - 7);
        doc.text(`Page ${i} of ${pageCount}`, pageW - MR, pageH - 7, { align: 'right' });
      }

      const projectSlug = filterProject !== 'all'
        ? `-${(projectOptions.find(([id]) => id === filterProject)?.[1] || 'project').replace(/\s+/g, '-')}`
        : '';
      doc.save(`procurement-tracker${projectSlug}.pdf`);
    } finally {
      setPdfExporting(false);
    }
  }

  /* --------------------------------------------------------
     Render
  -------------------------------------------------------- */
  return (
    <div className="report-shell">
      <div className="report-shell-inner">
        {/* Breadcrumb */}
        <div className="report-breadcrumb">Reports / Procurement Tracker</div>

        {/* Toolbar */}
        <div className="report-toolbar">
          <div className="report-title-wrap">
            <h1 className="report-title">Procurement Tracker</h1>
            <p className="report-subtitle">
              All ordered products across projects and vendors — edit status, dates and tracking in
              place, or click a row to open its PO
            </p>
          </div>

          <div className="report-actions">
            <button className="secondary-btn" onClick={exportExcel}>
              Export Excel
            </button>
            <button
              className="secondary-btn"
              onClick={exportPDF}
              disabled={pdfExporting}
            >
              {pdfExporting ? 'Generating PDF…' : 'Export PDF'}
            </button>
            <div className="col-picker-wrap" ref={colPanelRef}>
              <button
                className="secondary-btn"
                onClick={() => setShowColPanel((v) => !v)}
              >
                Hide Columns{hiddenCols.size > 0 ? ` (${hiddenCols.size})` : ''}
              </button>
              {showColPanel && (
                <div className="col-picker-panel">
                  <div className="col-picker-header">
                    <span>Columns</span>
                    {hiddenCols.size > 0 && (
                      <button
                        className="col-picker-reset"
                        onClick={() => setHiddenCols(new Set())}
                      >
                        Show all
                      </button>
                    )}
                  </div>
                  {ALL_COLUMNS.map((col) => (
                    <label key={col.key} className="col-picker-item">
                      <input
                        type="checkbox"
                        checked={!hiddenCols.has(col.key)}
                        onChange={() => toggleCol(col.key)}
                      />
                      {col.label}
                    </label>
                  ))}
                  {columnsResized && (
                    <div className="col-picker-footer">
                      <button className="col-picker-reset" onClick={resetColWidths}>
                        Reset column widths
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="report-filters">
          <div className="report-filter-group">
            <label className="report-filter-label">Project</label>
            <select
              className="report-filter-select"
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
            >
              <option value="all">All Projects</option>
              {projectOptions.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div className="report-filter-group">
            <label className="report-filter-label">Vendor</label>
            <select
              className="report-filter-select"
              value={filterVendor}
              onChange={(e) => setFilterVendor(e.target.value)}
            >
              <option value="all">All Vendors</option>
              {vendorOptions.map(([key, name]) => (
                <option key={key} value={key}>
                  {name}
                </option>
              ))}
              {hasRowsWithoutVendor && <option value="">No vendor</option>}
            </select>
          </div>

          <div className="report-filter-group">
            <label className="report-filter-label">Delivery Destination</label>
            <select
              className="report-filter-select"
              value={filterDelivery}
              onChange={(e) => setFilterDelivery(e.target.value)}
            >
              {deliveryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="report-filter-group">
            <label className="report-filter-label">Status</label>
            <select
              className="report-filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          <div className="report-filter-group">
            <label className="report-filter-label">Date Ordered</label>
            <input
              className="report-filter-select"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <input
              className="report-filter-select"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          {(filterProject !== 'all' || filterVendor !== 'all' || filterDelivery !== 'all' || filterStatus !== 'all' || startDate || endDate) && (
            <button
              className="report-filter-clear"
              onClick={() => {
                setFilterProject('all');
                setFilterVendor('all');
                setFilterDelivery('all');
                setFilterStatus('all');
                setStartDate('');
                setEndDate('');
              }}
            >
              Clear Filters
            </button>
          )}

          <span className="report-filter-count">
            {tableRows.length} item{tableRows.length !== 1 ? 's' : ''}
          </span>
        </div>

        {saveError && (
          <div className="tracker-save-error" role="alert">
            <span>{saveError}</span>
            <button type="button" onClick={() => setSaveError('')} aria-label="Dismiss">
              ×
            </button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="report-loading">Loading procurement items...</div>
        ) : (
          <div className="report-table-wrapper">
            <table
              ref={tableRef}
              className={`report-table${columnsResized ? ' is-resized' : ''}`}
              style={
                columnsResized
                  ? { width: visibleColumns.reduce((sum, c) => sum + colWidth(c.key), 0) }
                  : undefined
              }
            >
              {columnsResized && (
                <colgroup>
                  {visibleColumns.map((c) => (
                    <col key={c.key} style={{ width: colWidth(c.key) }} />
                  ))}
                </colgroup>
              )}
              <thead>
                <tr>
                  {visibleColumns.map((c) => (
                    <th key={c.key} data-col={c.key} title={columnsResized ? c.label : undefined}>
                      {c.label}
                      <span
                        className="col-resize-handle"
                        onPointerDown={(e) => startColResize(e, c.key)}
                        title="Drag to resize"
                        aria-hidden="true"
                      />
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {tableRows.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length} className="empty-row">
                      No procurement items found
                    </td>
                  </tr>
                ) : (
                  tableRows.map((r) => (
                    <tr
                      key={r.id}
                      className={r.poId && r.projectId ? 'row-linked' : undefined}
                      onClick={() => openPurchaseOrder(r)}
                      role={r.poId && r.projectId ? 'link' : undefined}
                      tabIndex={r.poId && r.projectId ? 0 : undefined}
                      title={r.poId && r.projectId ? `Open ${r.poNumber || 'purchase order'}` : undefined}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openPurchaseOrder(r);
                        }
                      }}
                    >
                      {show('image') && (
                        <td>
                          {r.image ? (
                            <img src={r.image} alt={r.itemName} className="item-thumb" />
                          ) : (
                            <div className="item-thumb placeholder" />
                          )}
                        </td>
                      )}
                      {show('itemName')       && <td>{r.itemName}</td>}
                      {show('vendor')         && <td>{r.vendorName}</td>}
                      {show('project')        && <td>{r.projectName || '-'}</td>}
                      {show('room')           && <td>{r.room || '-'}</td>}
                      {show('qty')            && <td>{r.qty}</td>}
                      {show('status') && (
                        <td className={savingClass(r, 'status')} {...stopRowNavigation}>
                          {r.itemId ? (
                            <select
                              className={`tracker-inline-select status-pill status-${statusSlug(r.orderStatus)}`}
                              value={r.statusSource === 'item' ? r.orderStatus : ''}
                              aria-label={`Status for ${r.itemName || 'item'}`}
                              onChange={(e) => saveTrackerEdit(r, { status: e.target.value })}
                            >
                              <option value="">{`${r.poStatus || 'No status'} (same as PO)`}</option>
                              {itemStatusChoices(
                                BUILT_IN_PO_STATUSES,
                                customOrderStatuses,
                                r.statusSource === 'item' ? r.orderStatus : ''
                              ).map((status) => (
                                <option key={status} value={status}>
                                  {status}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className={`status-pill status-${statusSlug(r.orderStatus)}`}>
                              {r.orderStatus || 'No status'}
                            </span>
                          )}
                        </td>
                      )}
                      {show('delivery') && (
                        <td>
                          <span className={`status-pill delivery-${deliveryPillClass(r.shipToType)}`}>
                            {deliveryPillClass(r.shipToType) === 'client'
                              ? 'Client Site'
                              : deliveryPillClass(r.shipToType) === 'warehouse'
                                ? 'Warehouse'
                                : getFreightDestinationLabel(r.shipToType, freightDestinations)}
                          </span>
                        </td>
                      )}
                      {show('dateOrdered')    && <td>{fmtDate(r.dateOrdered)}</td>}
                      {show('estDelivery') && (
                        <td className={savingClass(r, 'estimatedDeliveryDate')} {...stopRowNavigation}>
                          <TrackerInlineInput
                            type="date"
                            value={r.estimatedDeliveryDate}
                            ariaLabel={`Estimated delivery for ${r.poNumber || 'purchase order'}`}
                            onCommit={(value) => saveTrackerEdit(r, { estimatedDeliveryDate: value })}
                          />
                          {r.poItemCount > 1 && (
                            <div className="tracker-cell-hint">All {r.poItemCount} items on PO</div>
                          )}
                        </td>
                      )}
                      {show('confirmedShip') && (
                        <td className={savingClass(r, 'confirmedShipDate')} {...stopRowNavigation}>
                          <TrackerInlineInput
                            type="date"
                            value={r.confirmedShipDate}
                            ariaLabel={`Confirmed ship date for ${r.poNumber || 'purchase order'}`}
                            onCommit={(value) => saveTrackerEdit(r, { confirmedShipDate: value })}
                          />
                          {r.poItemCount > 1 && (
                            <div className="tracker-cell-hint">All {r.poItemCount} items on PO</div>
                          )}
                        </td>
                      )}
                      {show('actualDelivery') && (
                        r.itemId ? (
                          <td className={savingClass(r, 'deliveredDate')} {...stopRowNavigation}>
                            <TrackerInlineInput
                              type="date"
                              value={r.actualDeliveryDate}
                              ariaLabel={`Actual delivery for ${r.itemName || 'item'}`}
                              onCommit={(value) => saveTrackerEdit(r, { deliveredDate: value })}
                            />
                            {r.actualDeliverySource === 'po' && r.actualDeliveryDate && (
                              <div className="tracker-cell-hint">From PO</div>
                            )}
                          </td>
                        ) : (
                          <td>{fmtDate(r.actualDeliveryDate)}</td>
                        )
                      )}
                      {show('poNumber') && (
                        <td>
                          {r.poNumber}
                          {r.poTitle && <div className="tracker-cell-hint">{r.poTitle}</div>}
                        </td>
                      )}
                      {show('poBalance')      && <td>{formatCurrency(r.poBalance)}</td>}
                      {show('tracking') && (
                        r.itemId ? (
                          <td className={savingClass(r, 'freightTracking')} {...stopRowNavigation}>
                            <TrackerInlineInput
                              value={r.trackingUrl}
                              placeholder="Add tracking"
                              ariaLabel={`Tracking for ${r.itemName || 'item'}`}
                              onCommit={(value) => saveTrackerEdit(r, { freightTracking: value })}
                            />
                            {r.trackingSource === 'po' && r.trackingUrl && (
                              <div className="tracker-cell-hint">From PO</div>
                            )}
                          </td>
                        ) : (
                          <td>{r.trackingUrl || '-'}</td>
                        )
                      )}
                      {show('shippingTo')     && <td>{r.shippingTo || '-'}</td>}
                      {show('notes')          && <td className="notes-cell">{r.notes || '-'}</td>}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
